import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown } from 'lucide-react';

/** A listbox styled like the roadmap's other toolbar controls. */
export function UnitPicker({
  unit,
  units,
  onChange,
}: {
  unit: number;
  units: number[];
  onChange: (unit: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(unit);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    listRef.current?.focus();
    const dismiss = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  const choose = (value: number) => {
    close();
    if (value !== unit) onChange(value);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const index = units.indexOf(active);
    const moves: Record<string, number> = {
      ArrowDown: Math.min(units.length - 1, index + 1),
      ArrowUp: Math.max(0, index - 1),
      Home: 0,
      End: units.length - 1,
    };
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.key in moves) setActive(units[moves[event.key]]);
    else if (event.key === 'Enter' || event.key === ' ') choose(active);
    else if (event.key === 'Escape') close();
  };

  return (
    <div className="rm-unit-picker" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Unit ${unit}, change unit`}
        onClick={() => {
          setActive(unit);
          setOpen(!open);
        }}
      >
        Unit {unit}
        <ChevronDown size={15} aria-hidden="true" />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          aria-label="Units"
          aria-activedescendant={`rm-unit-option-${active}`}
          onKeyDown={onKeyDown}
        >
          {units.map((value) => (
            <li
              key={value}
              id={`rm-unit-option-${value}`}
              role="option"
              aria-selected={value === unit}
              className={value === active ? 'is-active' : ''}
              onPointerEnter={() => setActive(value)}
              onClick={() => choose(value)}
            >
              Unit {value}
              {value === unit && <Check size={15} aria-hidden="true" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
